import { CSSProperties } from '@mantine/core';
import Link, { LinkProps } from 'next/link';
import { ReactNode } from 'react';

import { IAnchorProps, ZorroAnchor } from '../Anchor';

export interface ILinkProps extends LinkProps {
  children: ReactNode;
  anchorProps?: Omit<IAnchorProps, 'href' | 'download' | 'target'>;

  download?: IAnchorProps['download'];
  target?: IAnchorProps['target'];
  iconProps?: CSSProperties;
  isMasked?: boolean;
}

/**
 * @deprecated Use `Link` from `@zorro/design-system/v2` instead.
 * This component will be removed in the next major version.
 *
 * Migration guide:
 * - Replace `<ZorroLink>` with `<Link>`
 * - Update imports: `import { Link } from '@zorro/design-system/v2'`
 * - `anchorProps` removed - props go directly on component
 * - `iconProps` removed - use `leftSection`/`rightSection` with styled icons
 * - `masked` prop renamed to `isMasked` for FullStory PII/PHI masking
 * - New props: `variant` ('primary' | 'secondary'), `leftSection`, `rightSection`
 */
export const ZorroLink = ({
  href,
  prefetch,
  children,
  anchorProps,
  download = false,
  target,
  isMasked,
  ...props
}: ILinkProps) => {
  if (download) {
    return (
      <ZorroAnchor
        {...anchorProps}
        href={String(href)}
        download={download}
        target={target}
        isMasked={isMasked}
      >
        {children}
      </ZorroAnchor>
    );
  }

  const isSamePageAction = String(href).startsWith('#');

  return (
    <Link
      {...props}
      href={href}
      prefetch={isSamePageAction ? false : prefetch}
      passHref
      legacyBehavior
    >
      <ZorroAnchor {...anchorProps} target={target} isMasked={isMasked}>
        {children}
      </ZorroAnchor>
    </Link>
  );
};
